import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Sale, Ticket, Event, Supplier } from '../types';
import { format } from 'date-fns';

interface SalesImportRow {
  'Buyer Name': string;
  'Buyer Email'?: string;
  'Buyer Phone'?: string;
  'Sale Date': string | Date;
  'Event Name': string;
  'Category/Section': string;
  Block?: string;
  Row?: string;
  Seat?: string;
  Quantity: number;
  'Sale Price': number;
  Currency: string;
  'Payment Method': string;
  'Order Number'?: string;
  'Split Type'?: string;
  Restrictions?: string;
  Notes?: string;
  'Sale Type': 'private' | 'supplier';
  'Supplier Name'?: string;
}

interface InventoryImportRow {
  'Event Name': string;
  'Event Date': string | Date;
  'Category/Section': string;
  Block?: string;
  Row?: string;
  Seat?: string;
  Quantity: number;
  'Purchase Price': number;
  Currency: string;
  'List Price'?: number;
  'Split Type'?: string;
  Restrictions?: string;
  Notes?: string;
  'Source Type': 'site' | 'private' | 'supplier';
  'Supplier/Source Name'?: string;
}

export const generateSalesTemplate = () => {
  const template: SalesImportRow[] = [
    {
      'Buyer Name': 'John Doe',
      'Buyer Email': 'john@example.com',
      'Buyer Phone': '+1234567890',
      'Sale Date': format(new Date(), 'yyyy-MM-dd'),
      'Event Name': 'Example Event',
      'Category/Section': 'VIP',
      Block: 'A',
      Row: '10',
      Seat: '15-16',
      Quantity: 2,
      'Sale Price': 150.0,
      Currency: 'USD',
      'Payment Method': 'card',
      'Order Number': 'ORD-001',
      'Split Type': 'together',
      Restrictions: 'none',
      Notes: 'Example sale note',
      'Sale Type': 'private',
      'Supplier Name': '',
    },
  ];

  const ws = XLSX.utils.json_to_sheet(template);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sales Template');

  // Add column widths
  const colWidths = [
    { wch: 20 }, // Buyer Name
    { wch: 25 }, // Buyer Email
    { wch: 15 }, // Buyer Phone
    { wch: 12 }, // Sale Date
    { wch: 25 }, // Event Name
    { wch: 20 }, // Category/Section
    { wch: 10 }, // Block
    { wch: 10 }, // Row
    { wch: 10 }, // Seat
    { wch: 10 }, // Quantity
    { wch: 12 }, // Sale Price
    { wch: 10 }, // Currency
    { wch: 15 }, // Payment Method
    { wch: 15 }, // Order Number
    { wch: 15 }, // Split Type
    { wch: 15 }, // Restrictions
    { wch: 30 }, // Notes
    { wch: 12 }, // Sale Type
    { wch: 20 }, // Supplier Name
  ];
  ws['!cols'] = colWidths;

  // Add data validation sheet
  const validationData = [
    ['Valid Payment Methods:', 'cash', 'card', 'bankTransfer', 'other'],
    [
      'Valid Split Types:',
      'together',
      'singles',
      'pairs',
      'up-to-2',
      'up-to-3',
      'up-to-4',
      'any',
    ],
    ['Valid Sale Types:', 'private', 'supplier'],
    ['Valid Currencies:', 'USD', 'EUR', 'GBP', 'ILS'],
  ];
  const wsValidation = XLSX.utils.aoa_to_sheet(validationData);
  XLSX.utils.book_append_sheet(wb, wsValidation, 'Valid Values');

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
  const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });
  saveAs(blob, 'sales_import_template.xlsx');
};

export const generateInventoryTemplate = () => {
  const template: InventoryImportRow[] = [
    {
      'Event Name': 'Example Event',
      'Event Date': format(new Date(), 'yyyy-MM-dd'),
      'Category/Section': 'VIP',
      Block: 'A',
      Row: '10',
      Seat: '15-16',
      Quantity: 2,
      'Purchase Price': 100.0,
      Currency: 'USD',
      'List Price': 150.0,
      'Split Type': 'together',
      Restrictions: 'none',
      Notes: 'Example inventory note',
      'Source Type': 'supplier',
      'Supplier/Source Name': 'Example Supplier',
    },
  ];

  const ws = XLSX.utils.json_to_sheet(template);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Inventory Template');

  // Add column widths
  const colWidths = [
    { wch: 25 }, // Event Name
    { wch: 12 }, // Event Date
    { wch: 20 }, // Category/Section
    { wch: 10 }, // Block
    { wch: 10 }, // Row
    { wch: 10 }, // Seat
    { wch: 10 }, // Quantity
    { wch: 15 }, // Purchase Price
    { wch: 10 }, // Currency
    { wch: 15 }, // List Price
    { wch: 15 }, // Split Type
    { wch: 15 }, // Restrictions
    { wch: 30 }, // Notes
    { wch: 15 }, // Source Type
    { wch: 25 }, // Supplier/Source Name
  ];
  ws['!cols'] = colWidths;

  // Add data validation sheet
  const validationData = [
    [
      'Valid Split Types:',
      'together',
      'singles',
      'pairs',
      'up-to-2',
      'up-to-3',
      'up-to-4',
      'any',
    ],
    ['Valid Source Types:', 'supplier', 'private', 'site'],
    ['Valid Currencies:', 'USD', 'EUR', 'GBP', 'ILS'],
  ];
  const wsValidation = XLSX.utils.aoa_to_sheet(validationData);
  XLSX.utils.book_append_sheet(wb, wsValidation, 'Valid Values');

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
  const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });
  saveAs(blob, 'inventory_import_template.xlsx');
};

export const parseSalesExcel = async (
  file: File,
  events: Event[]
): Promise<Partial<Sale>[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json<SalesImportRow>(sheet);

        const parsedSales: Partial<Sale>[] = jsonData.map((row) => {
          // Find matching event
          const event = events.find(
            (e) => e.name.toLowerCase() === row['Event Name'].toLowerCase()
          );

          if (!event) {
            throw new Error(`Event "${row['Event Name']}" not found`);
          }

          return {
            buyerName: row['Buyer Name'],
            buyerEmail: row['Buyer Email'] || '',
            buyerPhone: row['Buyer Phone'] || '',
            saleDate: new Date(row['Sale Date']),
            eventId: event._id,
            category: row['Category/Section'],
            block: row['Block'] || '',
            row: row['Row'] || '',
            seat: row['Seat'] || '',
            quantity: Number(row['Quantity']) || 1,
            salePrice: Number(row['Sale Price']),
            currency: row['Currency'] || 'USD',
            paymentMethod: (row['Payment Method'] as any) || 'cash',
            orderNumber: row['Order Number'] || '',
            split: (row['Split Type'] as any) || 'together',
            restrictions: row['Restrictions'] || 'none',
            notes: row['Notes'] || '',
            saleType: (row['Sale Type'] as 'private' | 'supplier') || 'private',
            supplier: row['Supplier Name'] || undefined,
            status: 'confirmed' as const,
          };
        });

        resolve(parsedSales);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
};

export const parseInventoryExcel = async (
  file: File,
  events: Event[]
): Promise<Partial<Ticket>[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json<InventoryImportRow>(sheet);

        const parsedInventory: Partial<Ticket>[] = [];
        const errors: string[] = [];

        jsonData.forEach((row, index) => {
          try {
            // Validate required fields
            if (!row['Event Name']) {
              errors.push(`Row ${index + 2}: Event Name is required`);
              return;
            }
            if (!row['Category/Section']) {
              errors.push(`Row ${index + 2}: Category/Section is required`);
              return;
            }
            if (!row['Quantity'] || isNaN(Number(row['Quantity']))) {
              errors.push(`Row ${index + 2}: Valid Quantity is required`);
              return;
            }
            if (!row['Purchase Price'] || isNaN(Number(row['Purchase Price']))) {
              errors.push(`Row ${index + 2}: Valid Purchase Price is required`);
              return;
            }

            // Find matching event with improved matching
            const eventName = row['Event Name'].toString().trim();
            let event = events.find(
              (e) => e.name.toLowerCase() === eventName.toLowerCase()
            );

            // Try fuzzy matching if exact match fails
            if (!event) {
              event = events.find((e) => {
                const eName = e.name.toLowerCase();
                const searchName = eventName.toLowerCase();
                return eName.includes(searchName) || searchName.includes(eName);
              });
            }

            // Try matching by normalizing team names
            if (!event) {
              const normalizedSearch = eventName.toLowerCase()
                .replace(/\s+vs\s+/g, ' v ')
                .replace(/\s+v\s+/g, ' vs ')
                .replace(/fc\s/g, '')
                .replace(/\sfc/g, '');
              
              event = events.find((e) => {
                const normalizedEvent = e.name.toLowerCase()
                  .replace(/\s+vs\s+/g, ' v ')
                  .replace(/\s+v\s+/g, ' vs ')
                  .replace(/fc\s/g, '')
                  .replace(/\sfc/g, '');
                return normalizedEvent === normalizedSearch;
              });
            }

            if (!event) {
              errors.push(`Row ${index + 2}: Event "${eventName}" not found. Available events: ${events.map(e => e.name).slice(0, 3).join(', ')}${events.length > 3 ? '...' : ''}`);
              return;
            }

            // Parse currency
            let currency = (row['Currency'] || 'GBP').toString().toUpperCase();
            if (currency === 'POUNDS') currency = 'GBP';
            if (currency === 'DOLLARS') currency = 'USD';
            if (currency === 'EUROS') currency = 'EUR';

            // Parse split type - ensure it matches backend validation
            let splitType = (row['Split Type'] || 'together').toString().toLowerCase().trim();
            
            // Map common variations to valid values
            const splitMapping: { [key: string]: string } = {
              'together': 'all-together',
              'all-together': 'all-together',
              'singles': 'singles',
              'single': 'singles',
              'pairs': 'up-to-2',
              'up-to-2': 'up-to-2',
              'up-to-3': 'up-to-3',
              'up-to-4': 'up-to-4',
              '': 'all-together', // default for empty
            };
            
            splitType = splitMapping[splitType] || 'all-together';

            // Handle supplier info
            const supplierName = row['Supplier/Source Name']?.toString().trim();
            const sourceType = row['Source Type']?.toString().toLowerCase();
            
            let finalSourceType: 'site' | 'private' | 'supplier' = 'supplier';
            if (['supplier', 'private', 'site'].includes(sourceType)) {
              finalSourceType = sourceType as 'site' | 'private' | 'supplier';
            }

            // Ensure category is not empty (required by backend)
            const category = row['Category/Section']?.toString().trim();
            if (!category) {
              errors.push(`Row ${index + 2}: Category/Section cannot be empty`);
              return;
            }

            // Create ticket object with only DTO-allowed fields
            const ticket: any = {
              eventId: event._id,
              category: category,
              quantity: Number(row['Quantity']) || 1,
              purchasePrice: Number(row['Purchase Price']),
              split: splitType,
              sourceType: finalSourceType,
            };

            // Add optional fields only if they have values
            if (row['Block']?.toString()) {
              ticket.block = row['Block'].toString();
            }
            if (row['Row']?.toString()) {
              ticket.row = row['Row'].toString();
            }
            if (row['Seat']?.toString()) {
              ticket.seat = row['Seat'].toString();
            }
            if (currency && currency !== 'GBP') {
              ticket.currency = currency;
            }
            if (row['List Price'] && !isNaN(Number(row['List Price']))) {
              ticket.wantedPrice = Number(row['List Price']);
            }
            if (row['Restrictions']?.toString() && row['Restrictions'].toString() !== 'none') {
              ticket.restrictions = row['Restrictions'].toString();
            }
            if (supplierName) {
              ticket.supplier = supplierName;
            }

            // Log the ticket data for debugging
            console.log(`Row ${index + 2} ticket data:`, {
              eventId: ticket.eventId,
              category: ticket.category,
              split: ticket.split,
              currency: ticket.currency,
              sourceType: ticket.sourceType,
              supplier: ticket.supplier,
              quantity: ticket.quantity,
              purchasePrice: ticket.purchasePrice,
            });

            parsedInventory.push(ticket);
          } catch (error) {
            errors.push(`Row ${index + 2}: ${error instanceof Error ? error.message : 'Unknown error'}`);
          }
        });

        if (errors.length > 0) {
          console.error('Import validation errors:', errors);
          throw new Error(`Import failed with ${errors.length} errors:\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? '\n...' : ''}`);
        }

        resolve(parsedInventory);
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
};

// Helper function to convert string to ArrayBuffer
function s2ab(s: string): ArrayBuffer {
  const buf = new ArrayBuffer(s.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
  return buf;
}

// Export interfaces for better typing
interface ExportData {
  events?: Event[];
  sales?: Sale[];
  tickets?: Ticket[];
  suppliers?: Supplier[];
  fulfillments?: any[];
}

interface ExportOptions {
  includeEvents?: boolean;
  includeSales?: boolean;
  includeTickets?: boolean;
  includeSuppliers?: boolean;
  includeFulfillments?: boolean;
  filename?: string;
}

// Main export function for comprehensive data export
export const exportAllDataToExcel = (data: ExportData, options: ExportOptions = {}) => {
  const {
    includeEvents = true,
    includeSales = true,
    includeTickets = true,
    includeSuppliers = true,
    includeFulfillments = true,
    filename = `data_export_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.xlsx`
  } = options;

  const wb = XLSX.utils.book_new();

  // Export Events
  if (includeEvents && data.events?.length) {
    const eventsSheet = createEventsSheet(data.events);
    XLSX.utils.book_append_sheet(wb, eventsSheet, 'Events');
  }

  // Export Sales
  if (includeSales && data.sales?.length) {
    const salesSheet = createSalesSheet(data.sales);
    XLSX.utils.book_append_sheet(wb, salesSheet, 'Sales');
  }

  // Export Inventory/Tickets
  if (includeTickets && data.tickets?.length) {
    const ticketsSheet = createTicketsSheet(data.tickets);
    XLSX.utils.book_append_sheet(wb, ticketsSheet, 'Inventory');
  }

  // Export Suppliers
  if (includeSuppliers && data.suppliers?.length) {
    const suppliersSheet = createSuppliersSheet(data.suppliers);
    XLSX.utils.book_append_sheet(wb, suppliersSheet, 'Suppliers');
  }

  // Export Fulfillments
  if (includeFulfillments && data.fulfillments?.length) {
    const fulfillmentsSheet = createFulfillmentsSheet(data.fulfillments);
    XLSX.utils.book_append_sheet(wb, fulfillmentsSheet, 'Fulfillments');
  }

  // Add summary sheet
  const summarySheet = createSummarySheet(data);
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

  // Write and download
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
  const blob = new Blob([s2ab(wbout)], { type: 'application/octet-stream' });
  saveAs(blob, filename);
};

// Create Events sheet
function createEventsSheet(events: Event[]) {
  const exportData = events.map(event => ({
    'Event ID': event._id,
    'Event Name': event.name,
    'Date': format(new Date(event.date), 'yyyy-MM-dd'),
    'Time': event.time || '',
    'Venue': event.venue,
    'Address': event.address || '',
    'City': event.city || '',
    'Country': event.country || '',
    'Status': event.status,
    'Capacity': event.capacity || '',
    'Total Tickets': event.totalTickets || 0,
    'Sold Tickets': event.soldTickets || 0,
    'Available Tickets': event.availableTickets || 0,
    'Revenue': event.revenue || 0,
    'Description': event.description || '',
    'Created At': event.createdAt ? format(new Date(event.createdAt), 'yyyy-MM-dd HH:mm:ss') : '',
    'Updated At': event.updatedAt ? format(new Date(event.updatedAt), 'yyyy-MM-dd HH:mm:ss') : ''
  }));

  const ws = XLSX.utils.json_to_sheet(exportData);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 15 }, // Event ID
    { wch: 25 }, // Event Name
    { wch: 12 }, // Date
    { wch: 8 },  // Time
    { wch: 20 }, // Venue
    { wch: 25 }, // Address
    { wch: 15 }, // City
    { wch: 15 }, // Country
    { wch: 10 }, // Status
    { wch: 10 }, // Capacity
    { wch: 12 }, // Total Tickets
    { wch: 12 }, // Sold Tickets
    { wch: 15 }, // Available Tickets
    { wch: 12 }, // Revenue
    { wch: 30 }, // Description
    { wch: 20 }, // Created At
    { wch: 20 }  // Updated At
  ];

  return ws;
}

// Create Sales sheet
function createSalesSheet(sales: Sale[]) {
  const exportData = sales.map(sale => {
    const event = typeof sale.eventId === 'object' ? sale.eventId : null;
    const supplier = typeof sale.supplierId === 'object' ? sale.supplierId : null;
    
    return {
      'Sale ID': sale._id || sale.id,
      'Order Number': sale.orderNumber || '',
      'Event Name': event?.name || sale.eventName || '',
      'Event Date': event?.date ? format(new Date(event.date), 'yyyy-MM-dd') : '',
      'Buyer Name': sale.buyerName || '',
      'Buyer Email': sale.buyerEmail || '',
      'Buyer Phone': sale.buyerPhone || '',
      'Category': sale.category || '',
      'Block': sale.block || '',
      'Row': sale.row || '',
      'Seat': sale.seat || '',
      'Quantity': sale.quantity || 0,
      'Sale Price': sale.salePrice || 0,
      'Total Amount': sale.totalAmount || 0,
      'Net Profit': sale.netProfit || 0,
      'Commission': sale.commission || 0,
      'Currency': sale.currency || 'USD',
      'Payment Method': sale.paymentMethod,
      'Sale Type': sale.saleType || 'private',
      'Supplier': supplier?.name || '',
      'Split': sale.split || '',
      'Restrictions': sale.restrictions || '',
      'Status': sale.status,
      'Delivery Status': sale.deliveryStatus || '',
      'Sale Date': format(new Date(sale.saleDate), 'yyyy-MM-dd HH:mm:ss'),
      'Notes': sale.notes || '',
      'Created At': sale.createdAt ? format(new Date(sale.createdAt), 'yyyy-MM-dd HH:mm:ss') : '',
      'Updated At': sale.updatedAt ? format(new Date(sale.updatedAt), 'yyyy-MM-dd HH:mm:ss') : ''
    };
  });

  const ws = XLSX.utils.json_to_sheet(exportData);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 15 }, // Sale ID
    { wch: 15 }, // Order Number
    { wch: 25 }, // Event Name
    { wch: 12 }, // Event Date
    { wch: 20 }, // Buyer Name
    { wch: 25 }, // Buyer Email
    { wch: 15 }, // Buyer Phone
    { wch: 15 }, // Category
    { wch: 10 }, // Block
    { wch: 8 },  // Row
    { wch: 10 }, // Seat
    { wch: 8 },  // Quantity
    { wch: 12 }, // Sale Price
    { wch: 12 }, // Total Amount
    { wch: 12 }, // Net Profit
    { wch: 12 }, // Commission
    { wch: 8 },  // Currency
    { wch: 15 }, // Payment Method
    { wch: 12 }, // Sale Type
    { wch: 20 }, // Supplier
    { wch: 10 }, // Split
    { wch: 15 }, // Restrictions
    { wch: 10 }, // Status
    { wch: 15 }, // Delivery Status
    { wch: 20 }, // Sale Date
    { wch: 30 }, // Notes
    { wch: 20 }, // Created At
    { wch: 20 }  // Updated At
  ];

  return ws;
}

// Create Tickets/Inventory sheet
function createTicketsSheet(tickets: Ticket[]) {
  const exportData = tickets.map(ticket => {
    const event = typeof ticket.eventId === 'object' ? ticket.eventId : null;
    const supplier = typeof ticket.supplierId === 'object' ? ticket.supplierId : null;
    
    return {
      'Ticket ID': ticket._id || ticket.id,
      'Event Name': event?.name || ticket.eventName || '',
      'Event Date': event?.date ? format(new Date(event.date), 'yyyy-MM-dd') : 
                    ticket.eventDate ? format(new Date(ticket.eventDate), 'yyyy-MM-dd') : '',
      'Event Time': ticket.eventTime || '',
      'Venue': event?.venue || ticket.venue || '',
      'Category': ticket.category || '',
      'Block': ticket.block || '',
      'Section': ticket.section || '',
      'Row': ticket.row || '',
      'Seat': ticket.seat || '',
      'Quantity': ticket.quantity || 0,
      'Sold Quantity': ticket.soldQuantity || 0,
      'Available Quantity': (ticket.quantity || 0) - (ticket.soldQuantity || 0),
      'Purchase Price': ticket.purchasePrice || 0,
      'Wanted Price': ticket.wantedPrice || 0,
      'Currency': ticket.currency || 'USD',
      'Source Type': ticket.sourceType || 'private',
      'Supplier/Source': supplier?.name || ticket.supplier || '',
      'Split': ticket.split || '',
      'Restrictions': ticket.restrictions || '',
      'Status': ticket.status,
      'Created At': ticket.createdAt ? format(new Date(ticket.createdAt), 'yyyy-MM-dd HH:mm:ss') : '',
      'Updated At': ticket.updatedAt ? format(new Date(ticket.updatedAt), 'yyyy-MM-dd HH:mm:ss') : ''
    };
  });

  const ws = XLSX.utils.json_to_sheet(exportData);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 15 }, // Ticket ID
    { wch: 25 }, // Event Name
    { wch: 12 }, // Event Date
    { wch: 8 },  // Event Time
    { wch: 20 }, // Venue
    { wch: 15 }, // Category
    { wch: 10 }, // Block
    { wch: 15 }, // Section
    { wch: 8 },  // Row
    { wch: 10 }, // Seat
    { wch: 8 },  // Quantity
    { wch: 12 }, // Sold Quantity
    { wch: 15 }, // Available Quantity
    { wch: 15 }, // Purchase Price
    { wch: 15 }, // Wanted Price
    { wch: 8 },  // Currency
    { wch: 12 }, // Source Type
    { wch: 20 }, // Supplier/Source
    { wch: 10 }, // Split
    { wch: 15 }, // Restrictions
    { wch: 10 }, // Status
    { wch: 20 }, // Created At
    { wch: 20 }  // Updated At
  ];

  return ws;
}

// Create Suppliers sheet
function createSuppliersSheet(suppliers: Supplier[]) {
  const exportData = suppliers.map(supplier => ({
    'Supplier ID': supplier._id || supplier.id,
    'Name': supplier.name,
    'Contact Name': supplier.contactName || '',
    'Email': supplier.email || '',
    'Phone': supplier.phone || '',
    'Company': supplier.company || '',
    'Bank Name': supplier.bankName || '',
    'Account Name': supplier.accountName || '',
    'Account Number': supplier.accountNumber || '',
    'Sort Code': supplier.sortCode || '',
    'Routing Number': supplier.routingNumber || '',
    'IBAN': supplier.iban || '',
    'SWIFT Code': supplier.swiftCode || '',
    'Bank Country': supplier.bankCountry || '',
    'Account Type': supplier.accountType || '',
    'PayPal Email': supplier.paypalEmail || '',
    'Balance': supplier.calculatedBalance || 0,
    'Manual Adjustment': supplier.manualAdjustment || 0,
    'Calculated Balance': supplier.calculatedBalance || 0,
    'Total From Sales': supplier.totalFromSales || 0,
    'Sales Count': supplier.salesCount || 0,
    'Last Adjustment Note': supplier.lastAdjustmentNote || '',
    'Last Adjustment Date': supplier.lastAdjustmentDate ? 
      format(new Date(supplier.lastAdjustmentDate), 'yyyy-MM-dd HH:mm:ss') : '',
    'Notes': supplier.notes || '',
    'Created At': supplier.createdAt ? format(new Date(supplier.createdAt), 'yyyy-MM-dd HH:mm:ss') : '',
    'Updated At': supplier.updatedAt ? format(new Date(supplier.updatedAt), 'yyyy-MM-dd HH:mm:ss') : ''
  }));

  const ws = XLSX.utils.json_to_sheet(exportData);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 15 }, // Supplier ID
    { wch: 20 }, // Name
    { wch: 20 }, // Contact Name
    { wch: 25 }, // Email
    { wch: 15 }, // Phone
    { wch: 20 }, // Company
    { wch: 20 }, // Bank Name
    { wch: 20 }, // Account Name
    { wch: 20 }, // Account Number
    { wch: 12 }, // Sort Code
    { wch: 15 }, // Routing Number
    { wch: 25 }, // IBAN
    { wch: 15 }, // SWIFT Code
    { wch: 15 }, // Bank Country
    { wch: 12 }, // Account Type
    { wch: 25 }, // PayPal Email
    { wch: 12 }, // Balance
    { wch: 15 }, // Manual Adjustment
    { wch: 15 }, // Calculated Balance
    { wch: 15 }, // Total From Sales
    { wch: 12 }, // Sales Count
    { wch: 30 }, // Last Adjustment Note
    { wch: 20 }, // Last Adjustment Date
    { wch: 30 }, // Notes
    { wch: 20 }, // Created At
    { wch: 20 }  // Updated At
  ];

  return ws;
}

// Create Fulfillments sheet
function createFulfillmentsSheet(fulfillments: any[]) {
  const exportData = fulfillments.map(fulfillment => ({
    'Fulfillment ID': fulfillment._id || fulfillment.id,
    'Sale ID': fulfillment.saleId,
    'Ticket ID': fulfillment.ticketId,
    'Quantity': fulfillment.quantity || 0,
    'Type': fulfillment.type || 'manual',
    'Notes': fulfillment.notes || '',
    'Created At': fulfillment.createdAt ? format(new Date(fulfillment.createdAt), 'yyyy-MM-dd HH:mm:ss') : ''
  }));

  const ws = XLSX.utils.json_to_sheet(exportData);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 15 }, // Fulfillment ID
    { wch: 15 }, // Sale ID
    { wch: 15 }, // Ticket ID
    { wch: 10 }, // Quantity
    { wch: 10 }, // Type
    { wch: 30 }, // Notes
    { wch: 20 }  // Created At
  ];

  return ws;
}

// Create Summary sheet
function createSummarySheet(data: ExportData) {
  const summary = [
    ['Data Export Summary', ''],
    ['Generated Date', format(new Date(), 'yyyy-MM-dd HH:mm:ss')],
    ['', ''],
    ['Data Type', 'Count'],
    ['Events', data.events?.length || 0],
    ['Sales', data.sales?.length || 0],
    ['Inventory Items', data.tickets?.length || 0],
    ['Suppliers', data.suppliers?.length || 0],
    ['Fulfillments', data.fulfillments?.length || 0],
    ['', ''],
    ['Totals', ''],
    ['Total Revenue', data.sales?.reduce((sum, sale) => sum + (sale.totalAmount || 0), 0) || 0],
    ['Total Profit', data.sales?.reduce((sum, sale) => sum + (sale.netProfit || 0), 0) || 0],
    ['Total Tickets Available', data.tickets?.reduce((sum, ticket) => sum + ((ticket.quantity || 0) - (ticket.soldQuantity || 0)), 0) || 0],
    ['Total Tickets Sold', data.tickets?.reduce((sum, ticket) => sum + (ticket.soldQuantity || 0), 0) || 0],
    ['Active Events', data.events?.filter(event => event.status === 'active').length || 0]
  ];

  const ws = XLSX.utils.aoa_to_sheet(summary);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 25 }, // Label
    { wch: 20 }  // Value
  ];

  return ws;
}
